import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MdClose } from "react-icons/md";
import styles from "./Gallery.module.scss";
import { getGalleryEntry } from "../../utils/network/Crud";
import BackButton from "../../Components/BackButton";
import { ThreeDots } from "react-loader-spinner";
import useTranslation from "../../utils/i18n/useTranslation";

const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20, scale: 0.96 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: "easeOut" } },
};

const Gallery = () => {
  const [imageUrls, setImageUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      try {
        const galleryEntry = await getGalleryEntry();
        if (galleryEntry.data) {
          setImageUrls(galleryEntry.data?.images || []);
        }
      } catch (error) {
        console.error("Error fetching gallery: ", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <ThreeDots visible height="80" width="80" color="rgb(251,157,71)" radius="9" />
      </div>
    );
  }

  return (
    <div>
      <BackButton title={t("galleryTitle")} />
      <motion.div
        className={styles.galleryContainer}
        initial="hidden"
        animate="show"
        variants={staggerContainer}
      >
        {!!imageUrls?.length ? (
          [...imageUrls].reverse().map((url, index) => (
            <motion.div
              key={index}
              className={styles.imageCard}
              variants={fadeUp}
              whileTap={{ scale: 0.96 }}
              onClick={() => setActiveImage(url)}
            >
              <img src={url} alt={`Uploaded ${index}`} loading="lazy" />
            </motion.div>
          ))
        ) : (
          <div className={styles.imageCard}>
            <span>{t("galleryEmpty")}</span>
          </div>
        )}
      </motion.div>

      <AnimatePresence>
        {activeImage && (
          <motion.div
            className={styles.lightbox}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveImage(null)}
          >
            <motion.button
              className={styles.lightboxClose}
              onClick={() => setActiveImage(null)}
              aria-label={t("close")}
            >
              <MdClose />
            </motion.button>
            <motion.img
              src={activeImage}
              alt="Preview"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
